import * as React from 'react';
import { toast } from 'react-toastify';
import { ToastComponent, ToastType, ToastOptions } from '@common/components';

import { actions } from 'react-redux-form';
import { Action, Dispatch } from 'redux';

import { AppState } from '..';
import { PageViewModel } from '@models/common.models';
import { escrowService } from '@services/EscrowService';
import { EscrowOfficerViewModel, OfficerSearchViewModel } from '@models/EscrowOfficer';
import { contractService } from '@services/ContractService';
import { ContractSimpleViewModel } from '@models/Contract';
import { EscrowOfficerState } from './escrow-officer.reducer';
import { getEscrowOffice } from '../escrow-office/escrow-office.actions';
import { loadEscrowOffice } from '../contract/contract.actions';
import FormNames from '../_constants/FormNames.enum';
import { history } from '../index';
import { RouteDefinitions } from '../common/route-definitions';
import { getRoute } from '@common/route-definitions';

// We can use enums instead of type constants like in the example. This is a newer TS feature
export enum EscrowOfficerActionType {
  CreateEscrowOfficerStart = 'CreateEscrowOfficerStart',
  CreateEscrowOfficerSuccess = 'CreateEscrowOfficerSuccess',
  CreateEscrowOfficerFailure = 'CreateEscrowOfficerFailure',

  EditEscrowOfficerStart = 'EditEscrowOfficerStart',
  EditEscrowOfficerSuccess = 'EditEscrowOfficerSuccess',
  EditEscrowOfficerFailure = 'EditEscrowOfficerFailure',

  GetEscrowOfficerStart = 'GetEscrowOfficerStart',
  GetEscrowOfficerSuccess = 'GetEscrowOfficerSuccess',
  GetEscrowOfficerFailure = 'GetEscrowOfficerFailure',

  GetEscrowOfficerDetailStart = 'GetEscrowOfficerDetailStart',
  GetEscrowOfficerDetailSuccess = 'GetEscrowOfficerDetailSuccess',
  GetEscrowOfficerDetailFailure = 'GetEscrowOfficeDetailFailure',

  GetContractsByOfficerStart = 'GetContractsByOfficerStart',
  GetContractsByOfficerSuccess = 'GetContractsByOfficerSuccess',
  GetContractsByOfficerFailure = 'GetContractsByOfficerFailure',

  EscrowOfficerSearchInit = 'EscrowOfficerSearchInit',
  EscrowOfficerSearchSuccess = 'EscrowOfficerSearchSuccess',
  EscrowOfficerSearchFailure = 'EscrowOfficerSearchFailure'
}

export interface EscrowOfficerAction extends Action {
  readonly type: EscrowOfficerActionType;
  readonly errorMessage?: string;
  readonly createdID?: number;
  readonly statusMessage?: string;
}

/********* Get **************/
export interface GetEscrowOfficerDetailStartAction extends Action {
  readonly statusMessage: string;
  readonly type: EscrowOfficerActionType.GetEscrowOfficerDetailStart;
}

export interface GetEscrowOfficerDetailSuccessAction extends Action {
  readonly statusMessage: string;
  readonly escrowOfficerVM: EscrowOfficerViewModel;
  readonly type: EscrowOfficerActionType.GetEscrowOfficerDetailSuccess;
}

export interface GetEscrowOfficerDetailFailureAction extends Action {
  readonly statusMessage: string;
  readonly errorMessage: string;
  readonly type: EscrowOfficerActionType.GetEscrowOfficerDetailFailure;
}

export interface GetContractsByOfficerStartAction extends Action {
  readonly statusMessage: string;
  readonly type: EscrowOfficerActionType.GetContractsByOfficerStart;
}

export interface GetContractsByOfficerSuccessAction extends Action {
  readonly statusMessage: string;
  readonly type: EscrowOfficerActionType.GetContractsByOfficerSuccess;
  readonly result: PageViewModel<ContractSimpleViewModel>;
}

export interface GetContractsByOfficerFailureAction extends Action {
  readonly statusMessage: string;
  readonly errorMessage: string;
  readonly type: EscrowOfficerActionType.GetContractsByOfficerFailure;
}

/********** Search **********/
export interface EscrowOfficerSearchInitAction extends Action {
  readonly query: string | null;
  readonly type: EscrowOfficerActionType.EscrowOfficerSearchInit;
}
export interface EscrowOfficerSearchSuccessAction extends Action {
  readonly pageNumber: number;
  readonly pageSize: number;
  readonly orderBy: string;
  readonly result: PageViewModel<OfficerSearchViewModel>;
  readonly type: EscrowOfficerActionType.EscrowOfficerSearchSuccess;
}
export interface EscrowOfficerSearchFailureAction extends Action {
  readonly errorMessage: string;
  readonly type: EscrowOfficerActionType.EscrowOfficerSearchFailure;
}

export function getEscrowOfficer(id?: number, onSuccess?: Function) {
  return async (dispatch: Dispatch<AppState>) => {
    dispatch(actions.reset('escrowOfficerForm'));
    // dispatch(actions.reset('escrowInfoForm'));
    // Reset Escrow Info Section
    dispatch(actions.change('affiliatesInfoForm.escrowOfficerId', 0));
    dispatch(actions.change('affiliatesInfoForm.escrowOneTimeFax', ''));

    if (id) {
      dispatch(getEscrowOfficerStart());
      try {
        var escrowOfficerVM = await escrowService.getOfficer(id);
        if (onSuccess) {
          dispatch(getEscrowOffice(escrowOfficerVM.escrowOffice!.id, loadEscrowOffice));
          dispatch(onSuccess(escrowOfficerVM));
          dispatch(actions.change('affiliatesInfoForm.escrowOfficerId', escrowOfficerVM.id));
          dispatch(actions.change('affiliatesInfoForm.escrowId', escrowOfficerVM.escrowOffice!.id));
        } else {
          dispatch(getEscrowOfficerSuccess(escrowOfficerVM));
          dispatch(actions.change('escrowOfficerForm', escrowOfficerVM));
        }
      } catch (e) {
        dispatch(getEscrowOfficerFailure(e.toString()));
      }
    }
  };
}

export function getEscrowOfficerStart(): EscrowOfficerAction {
  // do something with the escrowofficer
  return {
    type: EscrowOfficerActionType.GetEscrowOfficerStart
  };
}

export function getEscrowOfficerSuccess(officer: EscrowOfficerViewModel): EscrowOfficerAction {
  // do something with the escrowofficer
  return {
    type: EscrowOfficerActionType.GetEscrowOfficerSuccess,
    statusMessage: 'Officer: ' + officer.id + 'loaded.'
  };
}

export function getEscrowOfficerFailure(errorMessage: string): EscrowOfficerAction {
  return {
    errorMessage: errorMessage,
    type: EscrowOfficerActionType.GetEscrowOfficerFailure
  };
}

export function getEscrowOfficerDetail(id: number) {
  return async (dispatch: Dispatch<AppState>) => {
    dispatch(getEscrowOfficerDetailStart());
    try {
      const escrowOfficerVM = await escrowService.getOfficer(id);
      dispatch(getEscrowOfficerDetailSuccess(escrowOfficerVM));
    } catch (e) {
      dispatch(getEscrowOfficerDetailFailure(e.toString()));
    }
  };
}

export function getEscrowOfficerDetailStart(): GetEscrowOfficerDetailStartAction {
  // do something with the escrowofficer
  return {
    statusMessage: 'Getting Escrow Officer Detail',
    type: EscrowOfficerActionType.GetEscrowOfficerDetailStart
  };
}

export function getEscrowOfficerDetailSuccess(officer: EscrowOfficerViewModel): GetEscrowOfficerDetailSuccessAction {
  // do something with the escrowofficer
  return {
    type: EscrowOfficerActionType.GetEscrowOfficerDetailSuccess,
    escrowOfficerVM: officer,
    statusMessage: 'Officer: ' + officer.id + 'loaded.'
  };
}

export function getEscrowOfficerDetailFailure(err: string): GetEscrowOfficerDetailFailureAction {
  return {
    type: EscrowOfficerActionType.GetEscrowOfficerDetailFailure,
    statusMessage: 'Failed to load officer.',
    errorMessage: err
  };
}

export function getContractsByOfficer(officerId: number) {
  return async (dispatch: Dispatch<AppState>) => {
    dispatch(getContractsByOfficerStart());
    try {
      const result: PageViewModel<ContractSimpleViewModel> = await contractService.searchByEscrowOfficer(officerId);
      dispatch(getContractsByOfficerSuccess(result));
    } catch (e) {
      dispatch(getContractsByOfficerFailure(e.toString()));
    }
  };
}

export function getContractsByOfficerStart(): GetContractsByOfficerStartAction {
  return {
    statusMessage: 'Getting Escrow Officer Contracts',
    type: EscrowOfficerActionType.GetContractsByOfficerStart
  };
}

export function getContractsByOfficerSuccess(
  result: PageViewModel<ContractSimpleViewModel>
): GetContractsByOfficerSuccessAction {
  return {
    statusMessage: 'Got the contracts',
    type: EscrowOfficerActionType.GetContractsByOfficerSuccess,
    result: result
  };
}

export function getContractsByOfficerFailure(errorMessage: string): GetContractsByOfficerFailureAction {
  return {
    statusMessage: 'error',
    errorMessage: errorMessage,
    type: EscrowOfficerActionType.GetContractsByOfficerFailure
  };
}

export function createEscrowOfficer(agent: EscrowOfficerViewModel, isModal: boolean, saveCallback?: Function) {
  return async (dispatch: Dispatch<EscrowOfficerState>) => {
    dispatch(createEscrowOfficerStart());

    escrowService
      .saveOfficer(agent)
      .then(id => {
        dispatch(createEscrowOfficerSuccess(id, isModal));
        if (saveCallback) {
          saveCallback(id);
        } else {
          dispatch(actions.reset(FormNames.EscrowOfficerForm));
        }
      })
      .catch(err => dispatch(createEscrowOfficerFailure(err.message)));
  };
}

export function createEscrowOfficerStart(): EscrowOfficerAction {
  return {
    type: EscrowOfficerActionType.CreateEscrowOfficerStart,
    statusMessage: 'Creating new Escrow Officer'
  };
}

export function createEscrowOfficerSuccess(id: number, isModal: boolean): EscrowOfficerAction {
  toast.success(
    <ToastComponent type={ToastType.Success} message={'Escrow Officer Created Successfully'} />,
    ToastOptions.Success
  );
  if (!isModal) {
    history.push(getRoute(RouteDefinitions.escrowofficer.Details, { id: id }));
  }
  return {
    type: EscrowOfficerActionType.CreateEscrowOfficerSuccess,
    createdID: id,
    statusMessage: 'Successfully created officer: ' + id
  };
}

export function createEscrowOfficerFailure(message: string): EscrowOfficerAction {
  toast.error(
    <ToastComponent type={ToastType.Error} message={'Network error, unable to create escrow officer. ' + message} />,
    ToastOptions.Error
  );

  return {
    type: EscrowOfficerActionType.CreateEscrowOfficerFailure,
    errorMessage: message,
    statusMessage: 'Error Creating officer'
  };
}

export function editEscrowOfficer(
  id: number,
  agent: EscrowOfficerViewModel,
  isModal: boolean,
  saveCallback?: Function
) {
  return async (dispatch: Dispatch<EscrowOfficerState>) => {
    dispatch(editEscrowOfficerStart());
    escrowService
      .saveOfficer(agent)
      .then(() => {
        dispatch(editEscrowOfficerSuccess(id));
        if (saveCallback) {
          saveCallback(id);
        }
      })

      .catch(err => {
        dispatch(editEscrowOfficerFailure(err.message));
      });
  };
}

export function editEscrowOfficerStart(): EscrowOfficerAction {
  return {
    type: EscrowOfficerActionType.EditEscrowOfficerStart,
    statusMessage: 'Editing officer...'
  };
}

export function editEscrowOfficerSuccess(id: number): EscrowOfficerAction {
  toast.success(
    <ToastComponent type={ToastType.Success} message={'Escrow Officer Updated Successfully'} />,
    ToastOptions.Success
  );

  return {
    type: EscrowOfficerActionType.EditEscrowOfficerSuccess,
    createdID: id,
    statusMessage: 'Successfully edited officer: ' + id
  };
}

export function editEscrowOfficerFailure(message: string): EscrowOfficerAction {
  toast.error(
    <ToastComponent type={ToastType.Error} message={'Network error, unable to update escrow officer. ' + message} />,
    ToastOptions.Error
  );

  return {
    type: EscrowOfficerActionType.EditEscrowOfficerFailure,
    errorMessage: message,
    statusMessage: 'Failed to edit officer'
  };
}

export function lookupOffice(value: string, id: number) {
  return escrowService.searchOffices(value, id);
}

/********** Search **********/
export const escrowOfficerSearch = (
  query: string | null,
  pageSize: number,
  pageNumber: number,
  orderBy: string,
  orderDirection: string
) => {
  return async (dispatch: Dispatch<EscrowOfficerState>) => {
    dispatch(escrowOfficerSearchInit(query));
    try {
      let result: PageViewModel<OfficerSearchViewModel> = await escrowService.searchOfficersFull(
        query,
        pageSize,
        pageNumber,
        orderBy,
        orderDirection
      );
      dispatch(escrowOfficerSearchSuccess(pageNumber, pageSize, orderBy, result));
    } catch (e) {
      dispatch(escrowOfficerSearchFailure(e.toString()));
    }
  };
};
export function escrowOfficerSearchInit(query: string | null): EscrowOfficerSearchInitAction {
  return {
    query: query,
    type: EscrowOfficerActionType.EscrowOfficerSearchInit
  };
}
export function escrowOfficerSearchFailure(errorMessage: string): EscrowOfficerSearchFailureAction {
  toast.error(
    <ToastComponent
      type={ToastType.Error}
      message={'Network error, unable to search escrow officer. ' + errorMessage}
    />,
    ToastOptions.Error
  );

  return {
    errorMessage: errorMessage,
    type: EscrowOfficerActionType.EscrowOfficerSearchFailure
  };
}
export function escrowOfficerSearchSuccess(
  pageNumber: number,
  pageSize: number,
  orderBy: string,
  result: PageViewModel<OfficerSearchViewModel>
): EscrowOfficerSearchSuccessAction {
  return {
    pageNumber: pageNumber,
    pageSize: pageSize,
    orderBy: orderBy,
    result: result,
    type: EscrowOfficerActionType.EscrowOfficerSearchSuccess
  };
}



// WEBPACK FOOTER //
// ./src/escrow-officer/escrow-officer.actions.tsx